/**
 * The generated character sheet, rendered inside a cast member's card (CSH-001).
 *
 * Signs the sheet's storage key the same way the cast documents sign theirs
 * (default dashboard media bucket + `video-generation/` key). Signed URLs
 * expire at an hour, so a failed load refreshes the signature once — the
 * ParallaxPreview idiom. Clearly labeled as generated: it sits beside the real
 * member photos and must never read as one of them.
 */

import { Sparkles } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";

import { getDefaultAwsMediaBucket } from "../providers/aws/client";
import type { CharacterSheet } from "./characterSheets";
import { useMediaUrl } from "./useMediaUrl";

export const CharacterSheetFigure = ({
  sheet,
}: {
  sheet: CharacterSheet | undefined;
}) => {
  const url = useMediaUrl(
    sheet
      ? {
          storage_bucket: getDefaultAwsMediaBucket(),
          storage_path: sheet.sheetKey,
          storage_is_public: false,
        }
      : null,
  );
  const [failed, setFailed] = useState(false);
  const [retried, setRetried] = useState(false);
  if (!sheet) return null;
  return (
    <figure
      data-testid="character-sheet"
      className="space-y-1 rounded-md border bg-muted/30 p-2"
    >
      <figcaption className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge
          variant="outline"
          className="gap-1 border-violet-500 text-violet-600"
        >
          <Sparkles className="size-3" />
          character sheet
        </Badge>
        AI-generated reference from {sheet.referenceCount ??
          "this member's"}{" "}
        photo{sheet.referenceCount === 1 ? "" : "s"} of this{" "}
        {sheet.subjectKind === "dog" ? "dog" : "person"} — not an uploaded
        photo.
      </figcaption>
      {url.url && !failed ? (
        <a href={url.url} target="_blank" rel="noreferrer">
          <img
            src={url.url}
            alt={`Generated character sheet for this ${sheet.subjectKind}`}
            className="w-full max-w-xl rounded border"
            onError={() => {
              // One refresh covers an expired signature; a second failure is a
              // real one and the placeholder text takes over.
              if (retried) {
                setFailed(true);
              } else {
                setRetried(true);
                url.refresh();
              }
            }}
            onLoad={() => setFailed(false)}
          />
        </a>
      ) : (
        <p className="px-1 py-4 text-center text-xs text-muted-foreground">
          {failed ? "The sheet could not be loaded." : "Loading the sheet…"}
        </p>
      )}
    </figure>
  );
};
